/**
 * Global API error handler for frontend.
 * Handles 401 (session expired → redirect to login),
 * 5xx (server error toast), and network errors (connection error toast).
 */
import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';
import { toastStore } from '../stores/toastStore';
import { authStore, POST_LOGIN_REDIRECT_KEY } from '../auth';

/**
 * Maps backend RFC 7807 errorCode values to i18n keys for a localized toast.
 * Only validation errors a user can actually trigger and act on belong here.
 */
const LOCALIZED_ERROR_CODES: Record<string, string> = {
  'document.invalid_assigned_to_member_id': 'errors.invalid_assigned_to_member_id',
  'household.duplicate_member_name': 'errors.duplicate_member_name',
  // Spec 083 E4/S13 (#469 fallout): Notes over the 10,000-character ceiling (PATCH .../metadata).
  'document.notes_too_long': 'errors.notes_too_long',
  // Review Spec 086/F2: create/update/remove keep going through this global handler (their wire
  // test — `followups.spec086.test.ts` "create() propagates a 409 via the shared error handler"
  // — requires it); `complete`/`reopen` suppress it instead (`api/followups.ts`) because those
  // need a *local* UI reset (a checkbox revert, F3) the global handler can't do.
  'followup.active_exists': 'pages.followups.error_active_exists',
  'followup.already_completed': 'pages.followups.error_already_completed',
  'followup.not_completed': 'pages.followups.error_not_completed',
  'followup.not_open': 'pages.followups.error_not_open',
  'followup.subject_required': 'pages.followups.error_subject_required',
  'followup.subject_too_long': 'pages.followups.error_subject_too_long',
  'followup.note_too_long': 'pages.followups.error_note_too_long',
  'followup.due_date_required': 'pages.followups.error_due_date_required',
  'followup.not_found': 'pages.followups.error_not_found',
  'followup.document_required': 'pages.followups.error_document_required',
};

/**
 * Custom error class that carries HTTP status information.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly statusText: string,
    public readonly errorCode?: string,
    /** Structured reason for the failure (e.g. ShareLinkGoneDto.reason on 410, Spec 015 E3). */
    public readonly reason?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Handles API errors globally: shows toasts and redirects on 401.
 * Always re-throws so callers can do additional handling if needed.
 */
export function handleApiError(error: unknown, response?: Response): never {
  const t = get(_);

  // 401 Unauthorized → save redirect target, logout + redirect
  if (response?.status === 401 || (error instanceof ApiError && error.status === 401)) {
    const currentPath = window.location.pathname;
    if (currentPath && currentPath !== '/login' && !currentPath.startsWith('/auth/')) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, currentPath);
    }
    authStore.logout();
    toastStore.error(t('errors.session_expired'));
    window.location.href = '/login';
    throw error instanceof Error ? error : new Error('Unauthorized');
  }

  // 5xx Server errors
  if (response && response.status >= 500) {
    toastStore.error(t('errors.server_error'));
    throw error instanceof Error ? error : new Error(response.statusText);
  }
  if (error instanceof ApiError && error.status >= 500) {
    toastStore.error(t('errors.server_error'));
    throw error;
  }

  // Network / connection errors (fetch throws TypeError on network failure)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    toastStore.error(t('errors.connection_error'));
    throw error;
  }

  // Known validation error codes → localized toast (RFC 7807 errorCode → i18n key).
  // The backend message is English; map specific codes to a translated user message.
  if (error instanceof ApiError && error.errorCode && error.errorCode in LOCALIZED_ERROR_CODES) {
    toastStore.error(t(LOCALIZED_ERROR_CODES[error.errorCode]));
    throw error;
  }

  // Other errors: show generic toast
  if (error instanceof Error) {
    toastStore.error(error.message || t('errors.generic'));
    throw error;
  }

  // Fallback
  const fallback = new Error(t('errors.generic'));
  toastStore.error(t('errors.generic'));
  throw fallback;
}
